import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';
import { withLogging } from './llmLogger';

const loadPrompt = async (fileName) => {
  try {
    const response = await fetch(`/prompts/${fileName}`);
    if (!response.ok) throw new Error(`Failed to load prompt file: ${fileName}`);
    return await response.text();
  } catch (error) {
    return null;
  }
};

export const LibrarianService = {
  /**
   * Process student questions about learning materials
   */

  async generateContentForActivity(activity) {
    try {
      // If you need profile or course details, you may need to fetch from Redux or pass as props. Here, we use activity fields if present.
      const topic = activity.title || activity.topic || activity.id;
      const studentProfile = activity.profile || activity.studentProfile || null;
      const topicInfo = activity.topicInfo || activity;
      // Call the real LLM resource generator
      const resources = await this.generateStudyResources(topic, studentProfile, topicInfo);
      // Defensive fallback: always return an array
      return Array.isArray(resources) ? resources : [];
    } catch (err) {
      return [];
    }
  },

  // ... other methods ...

  /**
   * Generate personalized study resources
   */
  async generateStudyResources(payload) {
    const endpoint = `${API_BASE_URL}/generate-resources`;

    return withLogging(
      'LibrarianService',
      'generateStudyResources',
      endpoint,
      payload,
      async () => {
        try {
          const finalPayload = {
            agentId: 'librarian',
            persona: payload?.persona || null,
            ...payload,
          };
          const prompt = await loadPrompt('librarianResourcesPrompt.txt');
          if (!prompt) throw new Error('Prompt load failed');

          const response = await fetch(endpoint, {
            method: 'POST',
            headers: DEFAULT_HEADERS,
            body: JSON.stringify(finalPayload),
          });

          if (!response.ok) {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          return data.resources;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Generate resources error:', error);
          return null;
        }
      }
    );
  },

  /**
   * Chat with video context (transcript-aware)
   * @param {Object} params - { message, transcript, currentTime, videoMetadata }
   * @returns {Promise<Object>} - { message, timestamp }
   */
  async chatWithVideoContext({ message, transcript, currentTime, videoMetadata }) {
    const endpoint = `${API_BASE_URL}/chat-video`;
    const payload = {
      agentId: 'librarian',
      persona: videoMetadata?.persona || null,
      message,
      context: {
        transcript: transcript || '',
        currentTime,
        videoTitle: videoMetadata?.title,
        videoDescription: videoMetadata?.description,
        courseName: videoMetadata?.ml_courses_staging?.title,
      },
    };

    return withLogging('LibrarianService', 'chatWithVideoContext', endpoint, payload, async () => {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return {
          message: data.response || data.message,
          timestamp: data.timestamp || null,
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Video chat error:', error);
        return {
          message:
            'I apologize, but I encountered an error processing your question. Please try again.',
          timestamp: null,
        };
      }
    });
  },

  /**
   * Recommend videos for a course
   * @param {Object} params - { courseId, courseTopic, userProgress }
   * @returns {Promise<Array>} - Array of video recommendations
   */
  async recommendVideosForCourse({ courseId, courseTopic, userProgress }) {
    const endpoint = `${API_BASE_URL}/recommend-videos`;
    const payload = {
      courseId,
      courseTopic,
      userProgress,
    };

    return withLogging(
      'LibrarianService',
      'recommendVideosForCourse',
      endpoint,
      payload,
      async () => {
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: DEFAULT_HEADERS,
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          return data.recommendations || [];
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Video recommendation error:', error);
          return [];
        }
      }
    );
  },

  /**
   * Summarize video content
   * @param {string} transcript - Video transcript
   * @param {number} duration - Video duration in seconds
   * @returns {Promise<Object>} - Summary object
   */
  async summarizeVideo(transcript, duration) {
    const endpoint = `${API_BASE_URL}/summarize-video`;
    const payload = {
      transcript,
      duration,
    };

    return withLogging('LibrarianService', 'summarizeVideo', endpoint, payload, async () => {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return data.summary || {};
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Video summarization error:', error);
        return null;
      }
    });
  },

  /**
   * Extract key concepts from video
   * @param {string} transcript - Video transcript
   * @returns {Promise<Array>} - Array of key concepts
   */
  async extractKeyConcepts(transcript) {
    try {
      const payload = { transcript };

      const response = await fetch(`${API_BASE_URL}/extract-concepts`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      return data.concepts || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Concept extraction error:', error);
      return [];
    }
  },

  /**
   * Generate quiz from video
   * @param {string} transcript - Video transcript
   * @param {string} difficulty - Quiz difficulty level
   * @returns {Promise<Array>} - Array of quiz questions
   */
  async generateQuizFromVideo(transcript, difficulty = 'medium') {
    try {
      const payload = {
        transcript,
        difficulty,
      };

      const response = await fetch(`${API_BASE_URL}/generate-quiz`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      return data.questions || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Quiz generation error:', error);
      return [];
    }
  },

  /**
   * Find topic in transcript
   * @param {string} transcript - Video transcript
   * @param {string} topic - Topic to find
   * @returns {Promise<Object>} - Timestamps and context
   */
  async findTopicInTranscript(transcript, topic) {
    try {
      const payload = {
        transcript,
        topic,
      };

      const response = await fetch(`${API_BASE_URL}/find-topic`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      return data.results || {};
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Topic search error:', error);
      return {};
    }
  },
};
